import React from "react";
import { motion } from "framer-motion";
import { MOCK_MY_DONATIONS } from "../lib/mockData";
import { Sidebar } from "../components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { formatCurrency, formatDateTime, truncateHash } from "../lib/utils";
import { Heart, DollarSign, Calendar, ArrowUpRight } from "lucide-react";

export default function MyDonationsPage() {
  const donations = MOCK_MY_DONATIONS;
  const totalDonated = donations.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <Sidebar />
      <div className="lg:pl-64 pt-16">
        <div className="p-6 max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-800">My Donations</h1>
            <p className="text-slate-500 mt-1">Track all your contributions and their impact</p>
          </motion.div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white"><CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"><DollarSign className="h-6 w-6 text-primary" /></div>
              <div><p className="text-sm text-slate-500">Total Donated</p><p className="text-2xl font-heading font-bold text-primary">{formatCurrency(totalDonated)}</p></div>
            </CardContent></Card>
            <Card className="bg-white"><CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"><Heart className="h-6 w-6 text-primary" /></div>
              <div><p className="text-sm text-slate-500">Donations Made</p><p className="text-2xl font-heading font-bold text-slate-800">{donations.length}</p></div>
            </CardContent></Card>
            <Card className="bg-white"><CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center"><Calendar className="h-6 w-6 text-green-600" /></div>
              <div><p className="text-sm text-slate-500">Projects Supported</p><p className="text-2xl font-heading font-bold text-slate-800">{new Set(donations.map(d => d.project_id)).size}</p></div>
            </CardContent></Card>
          </div>

          {/* Donation List */}
          <Card className="bg-white">
            <CardHeader><CardTitle className="font-heading">Donation History</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {donations.map((donation) => (
                  <div key={donation.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-100 hover:border-primary/30 transition-colors" data-testid={`donation-${donation.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Heart className="h-5 w-5 text-primary" /></div>
                      <div>
                        <p className="font-medium text-slate-800">{donation.project_title}</p>
                        <p className="text-xs text-slate-400">{formatDateTime(donation.created_at)}</p>
                        <code className="text-[10px] text-slate-400 font-mono">TX: {truncateHash(donation.transaction_hash)}</code>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">{formatCurrency(donation.amount)}</p>
                      <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-200 mt-1">Verified</Badge>
                    </div>
                  </div>
                ))}
                {donations.length === 0 && (
                  <div className="text-center py-12 text-slate-400"><Heart className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No donations yet. Support a project to see your history here.</p></div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
